import React, { useEffect, useState } from 'react';
import { Button, Typography, Card, Row, Col, Space, Tag } from 'antd';
import {
  TeamOutlined, UserOutlined, ArrowRightOutlined, EnvironmentOutlined,
  MailOutlined, StarOutlined, TrophyOutlined, SafetyOutlined, WhatsAppOutlined,
  RocketOutlined, CheckCircleOutlined, ThunderboltOutlined, HeartFilled,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AdBanner from '../../components/AdBanner';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';
const BRAND_DARKER = '#075E54';
const INK = '#0b1f1a';

// Card do hero com mockup ilustrativo do app (SVG puro, sem asset externo)
// Card de evento no feed do mockup — reuso pra render vários
function MockFeedCard({ c }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 8, padding: '6px 8px', marginBottom: 5,
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      display: 'flex', gap: 6, alignItems: 'center',
    }}>
      <div style={{
        width: 24, height: 24, borderRadius: 12, background: c.bg,
        display: 'grid', placeItems: 'center', color: '#fff', fontSize: 10,
        flexShrink: 0,
      }}>{c.emoji || '👤'}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'inline-block', fontSize: 6.5, padding: '1px 5px', borderRadius: 4,
          background: c.tagBg, color: c.tagFg, fontWeight: 700, marginBottom: 1,
        }}>{c.tag}</div>
        <div style={{ fontSize: 9, fontWeight: 700, color: INK, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>
        <div style={{ fontSize: 7.5, color: '#8a9a93', lineHeight: 1.1 }}>{c.sub}</div>
      </div>
      {c.badge && (
        <div style={{
          background: '#f6a723', color: '#fff', padding: '2px 5px',
          borderRadius: 4, fontSize: 7, fontWeight: 700, flexShrink: 0,
        }}>{c.badge}</div>
      )}
    </div>
  );
}

const MOCK_FEED = [
  { tag: 'Novo boleiro', tagBg: '#e6f7ff', tagFg: '#1890ff', bg: '#1890ff', title: 'Jonatan Lima', sub: 'Jardim América · 2h', badge: '4.0' },
  { tag: 'Convocação', tagBg: '#fff7e6', tagFg: '#fa8c16', bg: '#fa8c16', emoji: '📢', title: 'F.C Vale das Onças', sub: 'Jogo hoje 20:00 · agora', badge: null },
  { tag: 'MVP', tagBg: '#f9f0ff', tagFg: '#722ed1', bg: '#722ed1', emoji: '🏆', title: 'Marcos foi MVP', sub: 'no Turma 40+ · 4h', badge: '⭐' },
  { tag: 'Entrou pro time', tagBg: '#e6fffb', tagFg: '#13c2c2', bg: '#13c2c2', emoji: '✓', title: 'Renato entrou', sub: 'Racha da Sexta FC · 6h', badge: null },
  { tag: 'Novo time', tagBg: '#f6ffed', tagFg: '#25D366', bg: '#25D366', emoji: '⚽', title: 'Racha da Sexta', sub: 'Vila Madalena · 1d', badge: null },
];

function AppMockup() {
  return (
    <>
      {/* Animacoes globais do mockup */}
      <style>{`
        @keyframes fz-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-14px); }
        }
        @keyframes fz-glow {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.1); }
        }
        @keyframes fz-orbit {
          from { transform: rotate(0deg) translateX(140px) rotate(0deg); }
          to   { transform: rotate(360deg) translateX(140px) rotate(-360deg); }
        }
        @keyframes fz-orbit-rev {
          from { transform: rotate(0deg) translateX(160px) rotate(0deg); }
          to   { transform: rotate(-360deg) translateX(160px) rotate(360deg); }
        }
        .fz-mockup-wrap { animation: fz-float 6s ease-in-out infinite; }
        .fz-mockup-glow { animation: fz-glow 4s ease-in-out infinite; }
        .fz-mockup-badge { animation: fz-float 5s ease-in-out infinite; }
        .fz-mockup-badge-2 { animation: fz-float 7s ease-in-out infinite reverse; }
        @media (prefers-reduced-motion: reduce) {
          .fz-mockup-wrap, .fz-mockup-glow, .fz-mockup-badge, .fz-mockup-badge-2 {
            animation: none !important;
          }
        }
        @media (max-width: 767px) {
          .fz-mockup-badge, .fz-mockup-badge-2 { display: none !important; }
        }
      `}</style>

      <div style={{ position: 'relative', maxWidth: 260, margin: '0 auto', padding: '20px 0' }}>
        {/* Glow pulsante atras */}
        <div className="fz-mockup-glow" style={{
          position: 'absolute', inset: '-30px', borderRadius: 60,
          background: 'radial-gradient(circle, rgba(37,211,102,0.5), transparent 65%)',
          filter: 'blur(40px)', pointerEvents: 'none',
        }} />

        {/* Badges flutuantes decorativos */}
        <div className="fz-mockup-badge" style={{
          position: 'absolute', top: 30, right: -20, zIndex: 5,
          background: '#fff', borderRadius: 12, padding: '8px 10px',
          boxShadow: '0 12px 28px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
          display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: INK,
        }}>
          <span style={{ fontSize: 16 }}>🏆</span>
          <div>
            <div style={{ fontSize: 8, color: '#8a9a93', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Novo MVP</div>
            <div style={{ fontSize: 11, marginTop: 1 }}>Marcos</div>
          </div>
        </div>

        <div className="fz-mockup-badge-2" style={{
          position: 'absolute', bottom: 100, left: -30, zIndex: 5,
          background: '#fff', borderRadius: 12, padding: '8px 10px',
          boxShadow: '0 12px 28px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
          fontSize: 11, fontWeight: 700, color: INK,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ fontSize: 16 }}>⚽</span>
          <div>
            <div style={{ fontSize: 8, color: '#8a9a93', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Convocação</div>
            <div style={{ fontSize: 11, marginTop: 1 }}>hoje 20h</div>
          </div>
        </div>

        {/* Frame do celular com float */}
        <div className="fz-mockup-wrap" style={{
          position: 'relative', borderRadius: 42, padding: 6,
          background: 'linear-gradient(145deg, #1a1a1a, #0a0a0a)',
          boxShadow: '0 30px 80px rgba(0,0,0,0.5), inset 0 2px 4px rgba(255,255,255,0.1)',
        }}>
          <div style={{
            background: '#f5f7fa', borderRadius: 36, overflow: 'hidden',
            aspectRatio: '9/19.5', position: 'relative',
          }}>
            {/* Notch */}
            <div style={{
              position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)',
              width: 78, height: 20, background: '#0a0a0a', borderRadius: 12, zIndex: 10,
            }} />

            {/* Header verde (replica do CommunityLayout) */}
            <div style={{
              background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 100%)`,
              padding: '32px 12px 12px', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 22, height: 22, borderRadius: 7, background: 'rgba(255,255,255,0.2)', display: 'grid', placeItems: 'center', fontSize: 11 }}>⚽</div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800 }}>Futezap</div>
                  <div style={{ fontSize: 7, opacity: 0.8, letterSpacing: 1 }}>COMUNIDADE</div>
                </div>
              </div>
              <div style={{ width: 22, height: 22, borderRadius: 11, background: '#f6a723', border: '1.5px solid #fff' }} />
            </div>

            {/* Bloco de highlights (replica do CommunityHighlights) */}
            <div style={{ padding: 7 }}>
              <div style={{
                background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 100%)`,
                borderRadius: 10, padding: '7px 5px', color: '#fff',
                boxShadow: '0 6px 14px rgba(37, 211, 102, 0.35)',
              }}>
                <div style={{ fontSize: 7.5, opacity: 0.9, marginBottom: 3, textAlign: 'center' }}>Essa semana rolou</div>
                <div style={{ display: 'flex', justifyContent: 'space-around', gap: 3 }}>
                  {[
                    { emoji: '🥅', v: '187', l: 'Gols' },
                    { emoji: '⭐', v: '413', l: 'Aval.' },
                    { emoji: '⚽', v: '124', l: 'Peladas' },
                    { emoji: '🏆', v: '15', l: 'MVPs' },
                  ].map((m, i) => (
                    <div key={i} style={{
                      background: 'rgba(255,255,255,0.15)', borderRadius: 6, padding: '3px 2px',
                      textAlign: 'center', flex: 1, minWidth: 0,
                    }}>
                      <div style={{ fontSize: 9, lineHeight: 1 }}>{m.emoji}</div>
                      <div style={{ fontSize: 10, fontWeight: 800, marginTop: 1 }}>{m.v}</div>
                      <div style={{ fontSize: 6.5, opacity: 0.9 }}>{m.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Rolando na comunidade (replica do CommunityFeed compact) */}
            <div style={{ padding: '2px 7px 46px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5, padding: '0 2px' }}>
                <span style={{ color: BRAND, fontSize: 10 }}>🔥</span>
                <span style={{ fontSize: 9.5, fontWeight: 700, color: INK }}>Rolando na comunidade</span>
              </div>
              {MOCK_FEED.map((c, i) => <MockFeedCard key={i} c={c} />)}
            </div>

            {/* Nav bar inferior fake */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: '#fff', borderTop: '1px solid #eef1f4',
              padding: '5px 12px 8px', display: 'flex', justifyContent: 'space-around',
            }}>
              {['🏠', '🔥', '🔍', '📍', '✉️'].map((e, i) => (
                <div key={i} style={{
                  fontSize: 13, opacity: i === 1 ? 1 : 0.4,
                  color: i === 1 ? BRAND : '#999',
                }}>{e}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const STEPS = [
  {
    num: '01',
    title: 'Cria teu perfil',
    desc: 'Login com Google ou celular. Escolhe posição, nível, sua região. Leva 2 minutos.',
    color: BRAND,
  },
  {
    num: '02',
    title: 'Aparece pra galera',
    desc: 'Fica no mapa da tua região. Times próximos te veem, boleiros veem seu time.',
    color: BRAND_DARK,
  },
  {
    num: '03',
    title: 'Joga bola',
    desc: 'Convite chega no celular, você aceita e vai jogar. Depois cada um avalia.',
    color: BRAND_DARKER,
  },
];

const FEATURES = [
  { icon: <EnvironmentOutlined />, title: 'Mapa da região', desc: 'Times e boleiros no mapa. Filtra por distância, posição e nível.' },
  { icon: <MailOutlined />, title: 'Convite direto', desc: 'Sem correria no zap. Convite chega pronto pra aceitar.' },
  { icon: <StarOutlined />, title: 'Reputação real', desc: 'Cada jogo gera avaliação. Todo mundo vê quem é confiável.' },
  { icon: <TrophyOutlined />, title: 'MVP e stats', desc: 'Gols, assistências e MVP automáticos a cada jogo.' },
  { icon: <SafetyOutlined />, title: 'Endereço protegido', desc: 'Local exato só libera quando você e o time topam.' },
  { icon: <WhatsAppOutlined />, title: 'Push no celular', desc: 'Convite bate na tela. Você não perde jogo por besteira.' },
];

const DEPOIMENTOS = [
  { nome: 'Rafael', role: 'Meia, joga na quinta', text: 'Achei um racha bom perto de casa em uma semana. Antes ficava perguntando em grupo, era chato pra caramba.' },
  { nome: 'Jonatan', role: 'Organiza racha de sábado', text: 'Precisava de goleiro pra sábado às 8h. Convidei dois pelo mapa, um respondeu em 10 minutos. Salvou.' },
  { nome: 'Marcos', role: 'Volante, 4.5 no rating', text: 'Bem melhor que ficar fazendo pergunta em grupo de bairro. Aqui já vem com a fama do cara.' },
];

// Carrossel de valor logo depois do hero — alterna entre 2 perfis
// (jogador vs organizador), com auto-slide de 7s e navegacao por dots.
// Cada slide traz UM CTA claro pro perfil correspondente — evita CTA-do-CTA.
const VALUE_SLIDES = [
  {
    key: 'jogador',
    kicker: 'PRO BOLEIRO',
    title: 'Achar racha bom perto de casa',
    accent: 'sem correr no zap',
    sub: 'Time da tua região no mapa, convite direto no celular e ranking do teu jogo. Grátis pra sempre.',
    valuePoints: [
      { ic: '🗺️', t: 'Times perto de você no mapa', d: 'Filtra por distância, posição e nível — acha racha que combina.' },
      { ic: '📨', t: 'Convite direto pelo app', d: 'Time abre vaga, você recebe. Sem grupo de whats, sem enrolação.' },
      { ic: '⭐', t: 'Ranking, MVP e frequência', d: 'Suas estatísticas de cada jogo. Reputação que atrai time bom.' },
      { ic: '📰', t: 'Notícias do teu time', d: 'Fica por dentro de tudo que rola: MVP, novo cadastro, notícias.' },
    ],
    cta: 'Sou jogador — quero jogar',
    route: '/jogador',
    gradient: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
    accentColor: BRAND,
    bgFrom: BRAND_DARKER,
    bgTo: '#031f18',
    previewKind: 'player',
  },
  {
    key: 'organizador',
    kicker: 'PRO DONO DE TIME',
    title: 'Time organizado',
    accent: 'do jeito que boleiro merece',
    sub: 'Perfil público, convocação em tempo real, elenco e cobrança na palma da mão. Sem mensalidade — você compra crédito só quando envia.',
    valuePoints: [
      { ic: '🏆', t: 'Perfil público do time', d: 'Jogador encontra você no mapa e pede pra entrar. Você decide quem entra.' },
      { ic: '⚽', t: 'Convocação com campo animado', d: 'O jogador clica no email e a lista de confirmados atualiza em tempo real no painel.' },
      { ic: '👥', t: 'Elenco unificado', d: 'Cadastro do jogador uma vez só. Se já tá na comunidade, os dados vêm.' },
      { ic: '📋', t: 'Escalação, amistoso e cobrança', d: 'Monta os times antes do jogo, marca amistoso e cobra a galera pelo Pix.' },
    ],
    cta: 'Cadastrar meu time',
    route: '/time',
    gradient: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_DARKER} 100%)`,
    accentColor: '#eab308',
    bgFrom: '#082f26',
    bgTo: '#020c09',
    previewKind: 'organizer',
  },
];

function PlayerPreviewMockup() {
  return (
    <svg viewBox="0 0 260 320" style={{ width: '100%', maxWidth: 300, height: 'auto' }}>
      {/* Card com map + player card */}
      <rect x="0" y="0" width="260" height="320" fill="rgba(255,255,255,0.03)" rx="20"/>
      {/* Mini mapa */}
      <rect x="14" y="14" width="232" height="140" fill="#0a4a2e" rx="12"/>
      {[[50,50],[130,80],[200,55],[80,110],[190,120]].map(([x,y],i) => (
        <g key={i}>
          <circle cx={x} cy={y+14} r="10" fill="rgba(0,0,0,0.3)"/>
          <circle cx={x} cy={y} r="8" fill="#22c55e"/>
          <text x={x} y={y+3} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">⚽</text>
        </g>
      ))}
      {/* Card de convite */}
      <rect x="14" y="168" width="232" height="60" fill="#fff" rx="12"/>
      <rect x="24" y="180" width="40" height="36" fill="#22c55e" rx="8"/>
      <text x="44" y="203" textAnchor="middle" fontSize="18" fill="#fff">📨</text>
      <text x="72" y="192" fontSize="10" fontWeight="800" fill="#0b1f1a">Convite pra jogar</text>
      <text x="72" y="205" fontSize="8" fill="#5a6a63">Racha da Sexta · 20h</text>
      <rect x="72" y="210" width="60" height="14" rx="7" fill="#22c55e"/>
      <text x="102" y="220" textAnchor="middle" fontSize="8" fill="#fff" fontWeight="800">ACEITAR</text>
      {/* Stats card */}
      <rect x="14" y="242" width="232" height="64" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" rx="12"/>
      {[['⭐','4.6','Rating'],['⚽','12','Gols'],['🏆','3','MVP'],['📅','86%','Presença']].map(([e,n,l],i) => (
        <g key={i}>
          <text x={44 + i*57} y="264" textAnchor="middle" fontSize="16">{e}</text>
          <text x={44 + i*57} y="284" textAnchor="middle" fontSize="14" fontWeight="900" fill="#22c55e">{n}</text>
          <text x={44 + i*57} y="296" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.5)">{l}</text>
        </g>
      ))}
    </svg>
  );
}

function OrganizerPreviewMockup() {
  return (
    <svg viewBox="0 0 260 320" style={{ width: '100%', maxWidth: 300, height: 'auto' }}>
      <rect x="0" y="0" width="260" height="320" fill="rgba(255,255,255,0.03)" rx="20"/>
      {/* Quadra top-down mini */}
      <defs>
        <pattern id="vc-stripes" x="0" y="0" width="260" height="40" patternUnits="userSpaceOnUse">
          <rect width="260" height="40" fill="#0a4a2e"/>
          <rect y="20" width="260" height="20" fill="#0d5c39"/>
        </pattern>
      </defs>
      <rect x="14" y="14" width="232" height="220" fill="url(#vc-stripes)" rx="12"/>
      <rect x="22" y="22" width="216" height="204" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" rx="2"/>
      <line x1="22" y1="124" x2="238" y2="124" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5"/>
      <circle cx="130" cy="124" r="26" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5"/>
      <circle cx="130" cy="124" r="2" fill="rgba(255,255,255,0.85)"/>
      {/* Jogadores */}
      {[
        {x:80,y:70,c:'#22c55e'},{x:180,y:70,c:'#22c55e'},{x:60,y:130,c:'#22c55e'},
        {x:200,y:130,c:'#22c55e'},{x:130,y:170,c:'#eab308'},{x:90,y:200,c:'#22c55e'},{x:170,y:200,c:'#22c55e'},
        {x:130,y:32,c:'#94a3b8',small:true},
      ].map((p,i) => (
        <g key={i}>
          <rect x={p.x-6} y={p.y-4} width="12" height="14" rx="2" fill={p.c}/>
          <circle cx={p.x} cy={p.y-8} r="4" fill="#fbbf24"/>
        </g>
      ))}
      {/* Zone label */}
      <rect x="108" y="18" width="44" height="10" rx="5" fill="rgba(0,0,0,0.5)"/>
      <text x="130" y="25" textAnchor="middle" fontSize="6" fill="rgba(255,255,255,0.7)" fontWeight="800" letterSpacing="1.2">ESPERA</text>
      {/* Bottom summary */}
      <rect x="14" y="248" width="232" height="58" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" rx="12"/>
      {[['⚽','7','Em campo','#22c55e'],['🪑','1','Espera','#94a3b8'],['📧','0','Pendente','#eab308'],['🎫','7cr','Gasto','#f8fafc']].map(([e,n,l,c],i)=>(
        <g key={i}>
          <text x={44 + i*57} y="272" textAnchor="middle" fontSize="14">{e}</text>
          <text x={44 + i*57} y="290" textAnchor="middle" fontSize="12" fontWeight="900" fill={c}>{n}</text>
          <text x={44 + i*57} y="300" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.5)">{l}</text>
        </g>
      ))}
    </svg>
  );
}

function ValueCarousel({ nav }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setI(x => (x + 1) % VALUE_SLIDES.length), 7000);
    return () => clearInterval(id);
  }, [paused]);
  const s = VALUE_SLIDES[i];
  return (
    <section
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        background: `linear-gradient(180deg, ${s.bgFrom}, ${s.bgTo})`,
        padding: '72px 24px 60px', color: '#fff', position: 'relative',
        overflow: 'hidden', transition: 'background 0.6s ease',
      }}>
      {/* Orbs */}
      <div style={{
        position: 'absolute', top: -80, left: -80, width: 340, height: 340,
        borderRadius: '50%', background: s.accentColor, opacity: 0.14, filter: 'blur(90px)',
        transition: 'background 0.6s ease',
      }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'inline-flex', gap: 6, padding: '5px 14px', borderRadius: 999,
            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
            fontSize: 11, fontWeight: 800, letterSpacing: 1.5, marginBottom: 16,
            color: s.accentColor, transition: 'color 0.6s ease',
          }}>
            {s.kicker}
          </div>
          <h2 style={{
            fontSize: 'clamp(30px, 5vw, 48px)', fontWeight: 900, letterSpacing: -1.2,
            margin: 0, lineHeight: 1.05,
          }}>
            {s.title}<br />
            <span style={{ color: s.accentColor, transition: 'color 0.6s ease' }}>{s.accent}</span>
          </h2>
          <p style={{
            fontSize: 15, color: 'rgba(255,255,255,0.75)', maxWidth: 620,
            margin: '14px auto 0', lineHeight: 1.5,
          }}>{s.sub}</p>
        </div>

        <Row gutter={[36, 32]} align="middle">
          <Col xs={24} md={12}>
            <div style={{ display: 'grid', gap: 14 }}>
              {s.valuePoints.map((v, idx) => (
                <div key={idx} style={{
                  display: 'flex', gap: 14, padding: '12px 16px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 14, alignItems: 'flex-start',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                    background: 'rgba(255,255,255,0.08)',
                    display: 'grid', placeItems: 'center', fontSize: 20,
                  }}>{v.ic}</div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#fff', letterSpacing: -0.2 }}>{v.t}</div>
                    <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, marginTop: 2 }}>{v.d}</div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => nav(s.route)}
              style={{
                all: 'unset', cursor: 'pointer', display: 'block', width: '100%',
                boxSizing: 'border-box', textAlign: 'center', marginTop: 24,
                padding: '18px 22px', borderRadius: 14, background: s.gradient,
                color: '#fff', fontWeight: 900, fontSize: 16, letterSpacing: -0.3,
                boxShadow: `0 16px 30px -8px ${s.accentColor}55`,
                transition: 'transform 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {s.cta} →
            </button>
          </Col>

          <Col xs={24} md={12} style={{ textAlign: 'center' }}>
            <div style={{
              display: 'inline-block', maxWidth: 320, width: '100%',
              padding: 12, background: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 24,
              boxShadow: '0 30px 60px -20px rgba(0,0,0,0.6)',
            }}>
              {s.previewKind === 'player' ? <PlayerPreviewMockup /> : <OrganizerPreviewMockup />}
            </div>
          </Col>
        </Row>

        {/* Dots + tabs */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 40 }}>
          {VALUE_SLIDES.map((v, idx) => (
            <button key={v.key} onClick={() => setI(idx)}
              style={{
                all: 'unset', cursor: 'pointer',
                padding: '6px 14px', borderRadius: 999,
                background: idx === i ? 'rgba(255,255,255,0.15)' : 'transparent',
                border: idx === i ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(255,255,255,0.1)',
                fontSize: 11, fontWeight: 800, letterSpacing: 1,
                color: idx === i ? '#fff' : 'rgba(255,255,255,0.55)',
                transition: 'all 0.2s',
              }}>
              {v.kicker}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// Landing page premium da raiz da comunidade (comunidade.futezap.com.br/)
export default function CommunityRootLanding() {
  const nav = useNavigate();
  const [hl, setHl] = useState(null);

  useEffect(() => {
    // Numeros ao vivo da comunidade — endpoint autenticado. So chama se o
    // visitante tem sessao ativa; senao a banda simplesmente nao aparece.
    // Evita 401 na rota publica (que o interceptor da api tratava como
    // "expulsa pra /login" e quebrava a LP).
    if (!localStorage.getItem('community_token')) return;
    // Landing usa totais globais (nao janela semanal) — publico, sem auth
    api.get('/community/feed/highlights-total').then(r => setHl(r.data)).catch(() => {});
  }, []);

  return (
    <div style={{ background: '#fff', color: INK, overflowX: 'hidden' }}>
      <AdBanner publicMode screenOverride="/comunidade-landing" />

      {/* ══════════════════ HERO FULLSCREEN ══════════════════ */}
      <section style={{
        position: 'relative', minHeight: '100vh',
        background: `radial-gradient(ellipse at top left, ${BRAND_DARK}, transparent 50%),
                     radial-gradient(ellipse at top right, ${BRAND}, transparent 50%),
                     linear-gradient(180deg, ${BRAND_DARKER} 0%, #041a15 100%)`,
        overflow: 'hidden',
      }}>
        {/* Orbs de blur decorativos */}
        <div style={{
          position: 'absolute', top: -100, right: -100, width: 400, height: 400,
          borderRadius: '50%', background: BRAND, opacity: 0.25, filter: 'blur(80px)',
        }} />
        <div style={{
          position: 'absolute', bottom: -100, left: -100, width: 500, height: 500,
          borderRadius: '50%', background: BRAND_DARK, opacity: 0.3, filter: 'blur(100px)',
        }} />
        {/* Grid pattern sutil */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.15,
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'><path d='M40 0H0v40' fill='none' stroke='rgba(255,255,255,0.15)' stroke-width='1'/></svg>\")",
        }} />

        {/* Nav topo */}
        <div style={{
          position: 'relative', maxWidth: 1200, margin: '0 auto',
          padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#fff' }}>
            <img src="/pwa/icon-192.png" alt="Futezap" width={44} height={44}
              style={{ borderRadius: 12, boxShadow: '0 8px 20px rgba(0,0,0,0.35)' }} />
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: -0.5 }}>Futezap</div>
              <div style={{ fontSize: 10, opacity: 0.7, letterSpacing: 1, fontWeight: 600 }}>COMUNIDADE</div>
            </div>
          </div>
          <Button ghost onClick={() => nav('/jogador')} style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff' }}>
            Entrar
          </Button>
        </div>

        {/* Corpo do hero */}
        <div style={{
          position: 'relative', maxWidth: 1200, margin: '0 auto',
          padding: '20px 24px 60px 24px',
        }}>
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} md={13}>
              <div style={{
                display: 'inline-block', padding: '6px 14px', borderRadius: 100,
                background: 'rgba(37, 211, 102, 0.15)', border: '1px solid rgba(37, 211, 102, 0.4)',
                color: BRAND, fontSize: 12, fontWeight: 700, marginBottom: 20,
                letterSpacing: 0.3,
              }}>
                <ThunderboltOutlined /> A REDE DA PELADA
              </div>

              <h1 style={{
                fontSize: 'clamp(38px, 6vw, 68px)',
                fontWeight: 900, lineHeight: 1.05, color: '#fff', margin: 0,
                letterSpacing: -1.5,
              }}>
                Onde boleiros e times<br/>
                <span style={{
                  background: `linear-gradient(90deg, ${BRAND}, #a3e635)`,
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>se encontram!</span>
              </h1>

              <p style={{
                fontSize: 18, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6,
                marginTop: 20, maxWidth: 520,
              }}>
                Time e boleiro da tua região no mapa. Convite direto pelo celular,
                reputação transparente e nada de correria. De graça.
              </p>

              <Space size={12} style={{ marginTop: 32 }} wrap>
                <Button size="large" onClick={() => nav('/jogador')}
                  style={{
                    background: '#fff', color: BRAND_DARKER, fontWeight: 800,
                    height: 54, padding: '0 28px', fontSize: 15, borderRadius: 12,
                    boxShadow: '0 12px 30px rgba(255,255,255,0.15)',
                  }}>
                  <UserOutlined /> Sou jogador <ArrowRightOutlined />
                </Button>
                <Button size="large" onClick={() => nav('/time')}
                  style={{
                    background: BRAND, color: '#fff', fontWeight: 800, borderColor: BRAND,
                    height: 54, padding: '0 28px', fontSize: 15, borderRadius: 12,
                    boxShadow: '0 12px 30px rgba(37, 211, 102, 0.4)',
                  }}>
                  <TeamOutlined /> Cadastrar meu time
                </Button>
              </Space>

              <div style={{ marginTop: 28, display: 'flex', gap: 20, flexWrap: 'wrap', color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
                <span><CheckCircleOutlined style={{ color: BRAND }} /> Sem mensalidade</span>
                <span><CheckCircleOutlined style={{ color: BRAND }} /> Sem cartão</span>
                <span><CheckCircleOutlined style={{ color: BRAND }} /> Login com Google</span>
              </div>
            </Col>

            <Col xs={24} md={11}>
              <AppMockup />
            </Col>
          </Row>
        </div>
      </section>

      {/* ══════════════════ CARROSSEL DE VALOR ══════════════════ */}
      <ValueCarousel nav={nav} />

      {/* ══════════════════ BANDA DE NUMEROS VIVOS ══════════════════ */}
      {hl && (
        <section style={{
          background: '#0b1f1a', padding: '40px 24px',
          borderTop: '1px solid rgba(255,255,255,0.05)',
        }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: 11, letterSpacing: 2, marginBottom: 20, fontWeight: 700 }}>
              A COMUNIDADE EM NÚMEROS
            </div>
            <Row gutter={[16, 16]} justify="center">
              {[
                { label: 'Gols', v: hl.goals, emoji: '🥅' },
                { label: 'Avaliações', v: hl.players_rated, emoji: '⭐' },
                { label: 'Peladas', v: hl.games, emoji: '⚽' },
                { label: 'Convocações', v: hl.calls, emoji: '📢' },
                { label: 'Boleiros', v: hl.new_players, emoji: '👥' },
              ].filter(m => m.v > 0).map(m => (
                <Col xs={12} sm={8} md={4} key={m.label}>
                  <div style={{ textAlign: 'center', color: '#fff' }}>
                    <div style={{ fontSize: 28 }}>{m.emoji}</div>
                    <div style={{
                      fontSize: 36, fontWeight: 900, lineHeight: 1,
                      background: `linear-gradient(90deg, ${BRAND}, #a3e635)`,
                      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    }}>
                      {m.v.toLocaleString('pt-BR')}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{m.label}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </section>
      )}


      {/* ══════════════════ DEPOIMENTOS ══════════════════ */}
      <section style={{ padding: '80px 24px', background: '#f5f7fa' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{
              display: 'inline-block', padding: '4px 12px', borderRadius: 100,
              background: 'rgba(37, 211, 102, 0.1)', color: BRAND_DARK,
              fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 12,
            }}>QUEM JÁ USA</div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 900, margin: 0, letterSpacing: -1, color: INK }}>
              A galera tá jogando mais bola
            </h2>
          </div>
          <Row gutter={[20, 20]}>
            {DEPOIMENTOS.map((d, i) => (
              <Col xs={24} md={8} key={i}>
                <div style={{
                  padding: 24, borderRadius: 16, background: '#fff', height: '100%',
                  border: '1px solid #e8ecef', position: 'relative',
                }}>
                  <div style={{ fontSize: 48, color: BRAND, opacity: 0.3, lineHeight: 0.5, marginBottom: 8 }}>“</div>
                  <div style={{ fontSize: 14, lineHeight: 1.6, color: '#3a4a43', marginBottom: 16, fontStyle: 'italic' }}>
                    {d.text}
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 20,
                      background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
                      color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800,
                    }}>{d.nome[0]}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: INK }}>{d.nome}</div>
                      <div style={{ fontSize: 12, color: '#8a9a93' }}>{d.role}</div>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* ══════════════════ FUTEZAP PRO — BANNER ELEGANTE ══════════════════ */}
      <section style={{ padding: '60px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{
            padding: 40, borderRadius: 24, position: 'relative', overflow: 'hidden',
            background: `linear-gradient(135deg, ${BRAND_DARKER} 0%, ${BRAND_DARK} 100%)`,
            color: '#fff',
          }}>
            <div style={{
              position: 'absolute', inset: 0, opacity: 0.4,
              backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'><path d='M60 0H0v60' fill='none' stroke='rgba(255,255,255,0.08)' stroke-width='1'/></svg>\")",
            }} />
            <Row gutter={24} align="middle" style={{ position: 'relative' }}>
              <Col xs={24} md={16}>
                <div style={{
                  display: 'inline-block', padding: '4px 10px', borderRadius: 100,
                  background: 'rgba(255,255,255,0.15)', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 12,
                }}>ORGANIZA TIME?</div>
                <h3 style={{ fontSize: 28, fontWeight: 900, margin: '0 0 8px', letterSpacing: -0.5 }}>
                  Automatiza tudo com o Futezap Pro
                </h3>
                <p style={{ fontSize: 15, opacity: 0.85, margin: 0, maxWidth: 500 }}>
                  Convocação automática no zap, ranking, MVP e cobrança de mensalidade.
                  30 dias grátis.
                </p>
              </Col>
              <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                <Button size="large" onClick={() => window.open('https://futezap.com.br', '_blank', 'noopener')}
                  style={{
                    background: '#fff', color: BRAND_DARKER, fontWeight: 800,
                    height: 52, padding: '0 24px', borderRadius: 12,
                  }}>
                  Ver Futezap Pro <RocketOutlined />
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      </section>

      {/* ══════════════════ FAQ ══════════════════ */}
      <section style={{ padding: '80px 24px', background: '#f5f7fa' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontSize: 'clamp(26px, 4vw, 36px)', fontWeight: 900, margin: 0, letterSpacing: -1, color: INK }}>
              Perguntas frequentes
            </h2>
          </div>
          <Row gutter={[16, 16]}>
            {[
              { q: 'É pago?', a: 'Pra jogador é grátis mesmo — perfil, mapa, convite, avaliação e ranking. Pra organizador, cadastrar o time, montar elenco e criar convocação também não custa. Só quando você envia mensagem (email, whatsapp) usa crédito: 1 crédito = 1 whatsapp OU 10 emails.' },
              { q: 'Como faço o cadastro?', a: 'Login rápido com Google ou pelo whats via código. É 100% grátis, sem senha.' },
              { q: 'Preciso mostrar meu endereço?', a: 'Não. O mapa mostra só a tua região. Endereço certinho só libera quando você e o time topam se conhecer.' },
              { q: 'Posso ser jogador e dono de time?', a: 'Pode sim. Mesma conta, dois papéis. Cadastra o time depois que entrar.' },
            ].map((f, i) => (
              <Col xs={24} md={12} key={i}>
                <div style={{
                  padding: 20, borderRadius: 12, background: '#fff', height: '100%',
                  border: '1px solid #e8ecef',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: INK }}>{f.q}</div>
                  <div style={{ fontSize: 13, color: '#5a6a63', lineHeight: 1.6 }}>{f.a}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* ══════════════════ CTA FINAL BIG ══════════════════ */}
      <section style={{
        padding: '80px 24px',
        background: `linear-gradient(135deg, ${BRAND_DARKER} 0%, ${BRAND_DARK} 50%, ${BRAND} 100%)`,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-50%', left: '-20%', width: '60%', height: '200%',
          background: `radial-gradient(circle, ${BRAND} 0%, transparent 50%)`,
          opacity: 0.4, filter: 'blur(80px)',
        }} />
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', position: 'relative', color: '#fff' }}>
          <div style={{ fontSize: 50, marginBottom: 12 }}>⚽</div>
          <h2 style={{
            fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 900, margin: 0,
            letterSpacing: -1.5, lineHeight: 1.1,
          }}>
            Bora jogar bola?
          </h2>
          <p style={{ fontSize: 18, marginTop: 16, opacity: 0.9, maxWidth: 500, margin: '16px auto 0' }}>
            Em 2 minutos tá dentro. 100% de graça.
          </p>
          <Space size={12} wrap style={{ justifyContent: 'center', marginTop: 36 }}>
            <Button size="large" onClick={() => nav('/jogador')}
              style={{
                background: '#fff', color: BRAND_DARKER, fontWeight: 800,
                height: 56, padding: '0 32px', fontSize: 15, borderRadius: 12,
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              }}>
              <UserOutlined /> Sou jogador
            </Button>
            <Button size="large" onClick={() => nav('/time')}
              style={{
                background: 'transparent', color: '#fff', fontWeight: 800, border: '2px solid #fff',
                height: 56, padding: '0 32px', fontSize: 15, borderRadius: 12,
              }}>
              <TeamOutlined /> Sou dono de time
            </Button>
          </Space>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '30px 24px', background: '#041a15', color: 'rgba(255,255,255,0.5)',
        textAlign: 'center', fontSize: 12,
      }}>
        Feito no Brasil <HeartFilled style={{ color: '#ef4444' }} /> pra quem joga bola.
        <div style={{ marginTop: 6, fontSize: 11, opacity: 0.7 }}>© Futezap · Todos os direitos reservados</div>
      </footer>
    </div>
  );
}
